import React, { useRef } from 'react';

function fmt(t: number): string {
  let v = t;
  if (!isFinite(v) || v < 0) v = 0;
  const m = Math.floor(v / 60);
  const s = v - m * 60;
  return `${String(m).padStart(2, '0')}:${s.toFixed(1).padStart(4, '0')}`;
}

/* Double-stop threshold — two clicks within this window collapses to
 * "rewind to start". Long enough to be intentional, short enough to
 * not feel sticky if the user pauses and walks away. */
const DOUBLE_STOP_MS = 800;

interface TransportProps {
  playing: boolean;
  playhead: number;
  duration: number;
  /** Sorted ascending list of clip start times across both tracks.
   * Used by the back / next buttons to jump between clip boundaries. */
  clipStarts: number[];
  onTogglePlay: () => void;
  onSeek: (t: number) => void;
  onSetPlaying: (playing: boolean) => void;
}

export default function Transport({
  playing, playhead, duration, clipStarts,
  onTogglePlay, onSeek, onSetPlaying,
}: TransportProps) {
  const lastStopAt = useRef<number>(0);

  /* Stop:
   *  - First click while playing → pause, keep the playhead.
   *  - First click while already paused → arm a rewind window.
   *  - Second click within DOUBLE_STOP_MS while paused → seek to 0.
   * "Already paused" is a bit fuzzy — covers the user pausing via the
   * Play button and then tapping Stop to rewind, which is the natural
   * sequence we want to support. */
  function handleStop(): void {
    const now = Date.now();
    if (!playing && now - lastStopAt.current < DOUBLE_STOP_MS) {
      onSeek(0);
      lastStopAt.current = 0;
      return;
    }
    onSetPlaying(false);
    lastStopAt.current = now;
  }

  /* Back: jump to the start of the clip the playhead is currently
   * inside. If the playhead is already at (or very near) a clip start,
   * fall through to the PREVIOUS clip's start. Tap repeatedly to walk
   * backward through every clip boundary. */
  function handleBack(): void {
    const sorted = clipStarts.slice().sort((a, b) => a - b);
    if (sorted.length === 0) { onSeek(0); return; }
    const AT_BOUNDARY_TOL = 0.05;
    for (let i = sorted.length - 1; i >= 0; i--) {
      const s = sorted[i];
      if (s < playhead - AT_BOUNDARY_TOL) {
        // Playhead is well past this clip's start — jump to it.
        onSeek(s);
        return;
      }
      if (s <= playhead + AT_BOUNDARY_TOL) {
        // Playhead is at this clip's start — step to the previous one.
        onSeek(i > 0 ? sorted[i - 1] : 0);
        return;
      }
    }
    onSeek(0);
  }

  /* Next: jump to the next clip start strictly after the playhead. If
   * there is none, jump to the end of the timeline. */
  function handleForward(): void {
    const sorted = clipStarts.slice().sort((a, b) => a - b);
    const AT_BOUNDARY_TOL = 0.05;
    for (const s of sorted) {
      if (s > playhead + AT_BOUNDARY_TOL) { onSeek(s); return; }
    }
    onSeek(Math.max(duration || 0, 0));
  }

  const safeDur = Math.max(duration || 0, 0.01);
  const hasClips = clipStarts.length > 0;

  return (
    <div className="transport">
      {/* VCR-style cluster: prev clip ⏮ — play/pause — stop — next clip ⏭ */}
      <div className="transport-controls">
        <button
          type="button"
          className="ghost btn--icon-sm transport-step"
          onClick={handleBack}
          disabled={!hasClips}
          aria-label="Previous clip"
          title="Previous clip"
        >⏮</button>
        <button
          type="button"
          className="transport-play"
          onClick={onTogglePlay}
          aria-label={playing ? 'Pause' : 'Play'}
          title={playing ? 'Pause' : 'Play'}
        >
          {playing ? '❚❚' : '▶'}
        </button>
        <button
          type="button"
          className="ghost btn--icon-sm transport-stop"
          onClick={handleStop}
          aria-label="Stop"
          title="Stop (double-click rewinds)"
        >■</button>
        <button
          type="button"
          className="ghost btn--icon-sm transport-step"
          onClick={handleForward}
          disabled={!hasClips}
          aria-label="Next clip"
          title="Next clip"
        >⏭</button>
      </div>

      <span className="transport-time">{fmt(playhead)} / {fmt(safeDur)}</span>
    </div>
  );
}
